import { ArrowUpRight, MessageCircle } from "lucide-react";
import { CATEGORIES } from "@/lib/categories";
import { waLink } from "@/lib/store";

export default function Categories() {
    return (
        <section
            id="categories"
            data-testid="categories-section"
            className="relative bg-background py-24 md:py-32"
        >
            <div className="absolute inset-0 bg-blueprint-light opacity-60 pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
                    <div>
                        <p className="uppercase tracking-[0.3em] text-xs font-semibold text-safety mb-4">
                            /01 · Electrical & plumbing
                        </p>
                        <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-5xl lg:text-6xl text-charcoal max-w-2xl">
                            Wires, switches, pipes & fittings — our core range.
                        </h2>
                    </div>
                    <p className="text-muted-foreground max-w-md">
                        Built for electricians and plumbers on site. Tell us the wire gauge,
                        switch brand, pipe size or fitting you need — we'll confirm stock and
                        price on WhatsApp.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 auto-rows-[240px] md:auto-rows-[300px]">
                    {CATEGORIES.map((c, idx) => (
                        <CategoryCard key={c.slug} category={c} index={idx} />
                    ))}
                </div>
            </div>
        </section>
    );
}

function CategoryCard({ category, index }) {
    const { Icon, name, label, description, image, span, slug, items } = category;
    const msg = `Hi The Hardware Store, I would like to enquire about ${label} items.`;
    return (
        <a
            href={waLink(msg)}
            target="_blank"
            rel="noreferrer"
            data-testid={`category-card-${slug}`}
            className={`group relative overflow-hidden rounded-2xl border border-border bg-card hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ${span || ""} row-span-2 md:row-span-1`}
            style={{ animationDelay: `${index * 80}ms` }}
        >
            <div className="absolute inset-0">
                <img
                    src={image}
                    alt={name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/95 via-charcoal-900/60 to-charcoal-900/10" />
            </div>

            <div className="relative h-full p-5 md:p-6 flex flex-col justify-between text-white">
                <div className="flex items-center justify-between">
                    <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold bg-white/10 border border-white/15 backdrop-blur rounded-full px-3 py-1.5">
                        <Icon className="w-3.5 h-3.5" /> {label}
                    </span>
                    <span className="w-9 h-9 rounded-full bg-safety text-white grid place-items-center opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all">
                        <ArrowUpRight className="w-4 h-4" />
                    </span>
                </div>

                <div>
                    <h3 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight leading-tight">
                        {name}
                    </h3>
                    <p className="mt-2 text-sm text-white/75 max-w-md">{description}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {items.slice(0, 3).map((it) => (
                            <span key={it} className="text-[11px] px-2.5 py-1 rounded-full bg-white/10 border border-white/10 text-white/80">
                                {it}
                            </span>
                        ))}
                    </div>
                    <div className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-safety">
                        <MessageCircle className="w-3.5 h-3.5" /> Ask on WhatsApp
                    </div>
                </div>
            </div>
        </a>
    );
}
